import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { useAuth } from '@/lib/auth/auth-provider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { ForgotPasswordError, ResetPasswordError } from '@/lib/auth/errors';

import { ForgotPasswordRequestStep, type ForgotPasswordValues } from './ForgotPasswordRequestStep';
import { ResetPasswordStep } from './ResetPasswordStep';

/**
 * `/forgot[?email=...]` — single-page two-step flow.
 *
 * Step 1: ask for an email, call `ForgotPasswordCommand` (Cognito sends a
 * 6-digit code via SES). With `prevent_user_existence_errors = ENABLED`
 * on the user pool, Cognito does NOT reveal whether the email exists — both
 * paths return success. So we advance to step 2 regardless of whether the
 * email is real.
 *
 * Step 2: ask for the code + a new password, call
 * `ConfirmForgotPasswordCommand`. On success, redirect to `/signin` with
 * the email prefilled (via querystring) and a sonner toast.
 *
 * Layout mirrors `SignInPage` — single-column centered card on a muted
 * background, NOT the two-column dialog used for sign-up. Forgot password
 * is a recovery flow, not a deliberate commitment, so the lighter
 * presentation fits and outside-click navigation back to `/` is fine.
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { forgotPassword, confirmForgotPassword } = useAuth();

  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [topError, setTopError] = useState<string | null>(null);

  const handleRequestSubmit = async (values: ForgotPasswordValues): Promise<void> => {
    setTopError(null);
    const result = await forgotPassword(values.email);

    if (result.isErr()) {
      setTopError(forgotPasswordErrorMessage(result.error));
      return;
    }

    setEmail(values.email);
    setStep('reset');
  };

  const handleResetSubmit = async (code: string, newPassword: string): Promise<void> => {
    setTopError(null);
    const result = await confirmForgotPassword(email, code, newPassword);

    if (result.isErr()) {
      setTopError(resetPasswordErrorMessage(result.error));
      return;
    }

    toast.success('Password reset. Sign in with your new password.');
    void navigate(`/signin?email=${encodeURIComponent(email)}`);
  };

  const handleResend = async (): Promise<void> => {
    setTopError(null);
    const result = await forgotPassword(email);
    if (result.isErr()) {
      setTopError(forgotPasswordErrorMessage(result.error));
    }
  };

  return (
    <main className="bg-muted/30 flex min-h-screen items-center justify-center px-4 py-12">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-2">
          <CardTitle className="text-xl">
            {step === 'request' ? 'Reset your password' : 'Enter your code'}
          </CardTitle>
          <CardDescription>
            {step === 'request' ? (
              <>
                Remembered it?{' '}
                <Link to="/signin" className="text-primary hover:underline">
                  Sign in
                </Link>
              </>
            ) : (
              <>
                Wrong email?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setStep('request');
                    setTopError(null);
                  }}
                  className="text-primary hover:underline"
                >
                  Use a different email
                </button>
              </>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {step === 'request' ? (
            <ForgotPasswordRequestStep
              defaultEmail={email}
              onSubmit={handleRequestSubmit}
              topError={topError}
            />
          ) : (
            <ResetPasswordStep
              email={email}
              onSubmit={handleResetSubmit}
              onResend={handleResend}
              topError={topError}
            />
          )}
        </CardContent>
      </Card>
    </main>
  );
}

function forgotPasswordErrorMessage(error: ForgotPasswordError): string {
  switch (error.kind) {
    case 'too-many-requests':
      return 'Too many attempts. Please try again in a few minutes.';
    case 'not-authorized':
      // Cognito's own message is more specific than anything we'd write
      // (e.g. "User password cannot be reset in the current state.").
      return error.message;
    case 'invalid-email':
      return 'That email address looks invalid. Please double-check and try again.';
    case 'unknown':
      return error.message;
  }
}

function resetPasswordErrorMessage(error: ResetPasswordError): string {
  switch (error.kind) {
    case 'code-mismatch':
      return 'That code is incorrect. Double-check the email and try again.';
    case 'code-expired':
      return 'That code has expired. Click "Resend code" for a new one.';
    case 'invalid-code-format':
      return 'Verification codes are six digits. Check what you entered.';
    case 'invalid-password':
      return error.message;
    case 'too-many-requests':
      return 'Too many attempts. Please wait a minute before trying again.';
    case 'user-not-found':
      return 'No account for this email. Try signing up instead.';
    case 'unknown':
      return error.message;
  }
}
